/**
 * Edificações de bases abertas no SCADA 3D (US-3602) — Nexora Grid.
 *
 * Criado em: 26/10/2026 · Última modificação: 26/10/2026 · Versão: 1.0.0
 *
 * Uma caixa por edificação (base = área do contorno, altura = altura da base aberta ou 1 pavimento estimado), sobre o
 * relevo, numa única chamada de desenho (InstancedMesh) e com a cor do uso — a cidade inteira cabe sem pesar. Clique na
 * caixa = ficha da edificação (uso, altura, setor e nó). Comércios e serviços (pontos) ficam de fora no 3D: aparecem na Rede.
 */
import { useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { COR_USO, centro, type FeicaoEdificacao } from "../edificacoes";
import { alturaCena } from "./Cena";
import { projetar, type Rede } from "./rede";
import type { Relevo } from "./relevo";

export function Edificacoes3d({ lista, rede, relevo, aoSelecionar }: {
  lista: FeicaoEdificacao[]; rede: Rede; relevo: Relevo; aoSelecionar: (f: FeicaoEdificacao) => void;
}) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const edificios = useMemo(() => lista.filter((f) => f.properties.tipo === "edificacao"), [lista]);
  const geometria = useMemo(() => new THREE.BoxGeometry(1, 1, 1).translate(0, 0.5, 0), []);
  useLayoutEffect(() => {
    const malha = ref.current;
    if (!malha) return;
    const aux = new THREE.Object3D();
    const cor = new THREE.Color();
    edificios.forEach((f, i) => {
      const [lon, lat] = centro(f);
      const p = projetar(rede, lon, lat);
      const lado = Math.max(3, Math.sqrt(f.properties.area_m2 ?? 60));
      aux.position.set(p.x, alturaCena(relevo, rede, p.x, p.z), p.z);
      aux.scale.set(lado, Math.max(2.5, f.properties.altura_m ?? 3), lado);
      aux.updateMatrix();
      malha.setMatrixAt(i, aux.matrix);
      malha.setColorAt(i, cor.set(COR_USO[f.properties.uso] ?? COR_USO.desconhecido));
    });
    malha.instanceMatrix.needsUpdate = true;
    if (malha.instanceColor) malha.instanceColor.needsUpdate = true;
  }, [edificios, rede, relevo]);
  if (!edificios.length) return null;
  return (
    <instancedMesh ref={ref} args={[geometria, undefined, edificios.length]}
      onClick={(ev: { stopPropagation: () => void; instanceId?: number }) => {
        ev.stopPropagation();
        if (ev.instanceId !== undefined) aoSelecionar(edificios[ev.instanceId]);
      }}>
      <meshStandardMaterial roughness={0.85} metalness={0.05} />
    </instancedMesh>
  );
}
